import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import PageBody from "@components/PageBody";
import AccountService from "@services/Account";
import AuthenticationService from "@services/Authentication";
import AdminPageTemplate from "@templates/AdminPage";
import { formatUserDate, formatUserDatetime } from "@utils/datetime";
import { formatCNPJ, formatPhone } from "@utils/form";

const useStyles = makeStyles(() => ({
  infoItem: {
    display: "flex",
    alignItems: "center",
  },
  infoItemIcon: {
    marginRight: "5px",
  },
  shortcode: {
    marginLeft: "8px",

    "&:first-child": {
      marginLeft: "0px",
    },
  },
}));

const StateEnum = {
  PENDING_PROVISION: 1,
  PROVISIONED: 2,
  TRIAL: 3,
  ACTIVATED: 4,
  INACTIVATED: 5,
  CANCELLED: 6,
  PENDING_ACCOUNT: 7,
};

const regionLabels = {
  1: "Sudeste - São Paulo",
  2: "Sudeste - Rio de Janeiro/Espírito Santo",
  3: "Sudeste - Minas Gerais",
  4: "Nordeste",
  5: "Nordeste - Bahia/Sergipe",
  6: "Centro-Oeste",
  7: "Norte",
  8: "Sul - Paraná/Santa Catarina",
  9: "Sul - Rio Grande do Sul",
};

const accountTypeLabels = {
  1: "Empresa",
  2: "Integrador",
  3: "M2M",
  4: "Parceiro",
};

function Company(props) {
  const classes = useStyles();

  const [account, setAccount] = useState(null);
  const [shortcodes, setShortcodes] = useState([]);
  const [user, setUser] = useState(null);

  const stateData = {
    [StateEnum.PENDING_ACCOUNT]: {
      icon: (
        <ErrorOutlineIcon className={classes.infoItemIcon} color="secondary" />
      ),
      label: "Conta Incompleta",
    },
    [StateEnum.PENDING_PROVISION]: {
      icon: (
        <HourglassEmptyIcon
          className={classes.infoItemIcon}
          color="secondary"
        />
      ),
      label: "Provisão Pendente",
    },
    [StateEnum.PROVISIONED]: {
      icon: (
        <CheckCircleOutlineIcon
          className={classes.infoItemIcon}
          style={{ color: "#4caf50" }}
        />
      ),
      label: "Provisionado",
    },
    [StateEnum.TRIAL]: {
      icon: (
        <ErrorOutlineIcon className={classes.infoItemIcon} color="primary" />
      ),
      label: "Experimental",
    },
    [StateEnum.ACTIVATED]: {
      icon: (
        <PowerSettingsNewIcon
          className={classes.infoItemIcon}
          style={{ color: "#4caf50" }}
        />
      ),
      label: "Ativo",
    },
    [StateEnum.INACTIVATED]: {
      icon: (
        <PowerSettingsNewIcon
          className={classes.infoItemIcon}
          color="secondary"
        />
      ),
      label: "Inativo",
    },
    [StateEnum.CANCELLED]: {
      icon: <BlockIcon className={classes.infoItemIcon} color="secondary" />,
      label: "Cancelado",
    },
  };

  function renderStateLabel() {
    if (account) {
      const info = stateData[account.state];
      return (
        <Typography className={classes.infoItem} variant="body1">
          {info.icon}
          {info.label}
        </Typography>
      );
    }

    return <Typography variant="body1">---</Typography>;
  }

  function renderRegionLabel() {
    return (
      <Typography variant="body1">
        {account ? regionLabels[account.client.region] : "---"}
      </Typography>
    );
  }

  function renderAccountTypeLabel() {
    return (
      <Typography variant="body1">
        {account ? accountTypeLabels[account.accountType] : "---"}
      </Typography>
    );
  }

  function renderStartDateLabel() {
    return (
      <Typography variant="body1">
        {account ? formatUserDate(account.validity.start) : "---"}
      </Typography>
    );
  }

  function renderEndDateLabel() {
    return (
      <Typography variant="body1">
        {account && account.validity.end
          ? formatUserDate(account.validity.end)
          : "---"}
      </Typography>
    );
  }

  function renderBillingNumberLabel() {
    return (
      <Typography variant="body1">
        {account && account.billingNumber
          ? formatPhone(account.billingNumber)
          : "---"}
      </Typography>
    );
  }

  useEffect(() => {
    function loadAccountDetails() {
      AccountService.getAccountDetail()
        .then((response) => {
          setAccount(response.account);
          setShortcodes(response.shortcodes);
        })
        .catch(() => {
          toast.error("Erro ao carregar dados da empresa!");
        });
    }

    function loadUser() {
      AuthenticationService.currentAuthenticatedUser().then((user) => {
        if (user) {
          setUser(user);
        } else {
          toast.error("Erro ao carregar dados de usuário!");
        }
      });
    }

    loadAccountDetails();
    loadUser();
  }, []);

  return (
    <AdminPageTemplate breadcrumbs={props.breadcrumbs}>
      <PageBody title="Empresa">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">ID:</Typography>
            <Typography variant="body1">
              {account ? account.accountId : "---"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Status:</Typography>
            {renderStateLabel()}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">CNPJ:</Typography>
            <Typography variant="body1">
              {account ? formatCNPJ(account.client.taxId) : "---"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Regional:</Typography>
            {renderRegionLabel()}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Razão Social:</Typography>
            <Typography variant="body1">
              {account ? account.client.corporateName : "---"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Nome Fantasia:</Typography>
            <Typography variant="body1">
              {account && account.client.businessName
                ? account.client.businessName
                : "---"}
            </Typography>
          </Grid>
        </Grid>
      </PageBody>

      <PageBody title="Contrato">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Tipo</Typography>
            <Typography variant="body1">{renderAccountTypeLabel()}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Data de Início</Typography>
            {renderStartDateLabel()}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Data de Fim</Typography>
            {renderEndDateLabel()}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Mensagens por Mês</Typography>
            <Typography variant="body1">
              {account ? account.monthlyQuota : "---"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Taxa de Envio</Typography>
            <Typography variant="body1">
              {account ? account.rateLimit : "---"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Número de Tarifação</Typography>
            {renderBillingNumberLabel()}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Cabeçalho</Typography>
            <Typography variant="body1">
              {account && account.customHeader ? account.customHeader : "---"}
            </Typography>
          </Grid>
        </Grid>
      </PageBody>

      <PageBody title="Shortcodes">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {shortcodes.map((shortcode) => (
              <Chip
                className={classes.shortcode}
                label={shortcode.code}
                color="primary"
              />
            ))}
          </Grid>
        </Grid>
      </PageBody>

      <PageBody title="Usuário">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Nome:</Typography>
            <Typography variant="body1">
              {user && user.attributes.name ? user.attributes.name : "---"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">E-mail:</Typography>
            <Typography variant="body1">
              {user ? user.attributes.email : "---"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Login:</Typography>
            <Typography variant="body1">
              {user ? user.username : "---"}
            </Typography>
          </Grid>
          {/* custom:last_access attribute not implemented in Cognito.
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Último Acesso:</Typography>
            <Typography variant="body1">
              {user && user.attributes["custom:last_access"]
                ? formatUserDatetime(user.attributes["custom:last_access"])
                : "---"}
            </Typography>
          </Grid> */}
        </Grid>
      </PageBody>
    </AdminPageTemplate>
  );
}

Company.propTypes = {
  breadcrumbs: PropTypes.array,
};

Company.defaultProps = {
  breadcrumbs: [],
};

export default Company;
